import { queryOptions, useQuery } from '@tanstack/react-query'

import { equityMarketDataFetcher } from '@/libs/fetcher/presets'

/**
 * 24H sparkline for the Stocks tab (HomePageV2), copied from cdc-web-main's
 * equity history sparkline (MarketTabs/StocksList/EquityThumbnailChart.tsx).
 *
 * cdc-web-main uses equity-price-api `/v1/equity/history?minigraph=true`, which
 * is behind their BFF and requires auth. The public equivalent available here
 * is equity-market-data `/api/v1/chart` (same upstream family as the stock
 * detail page chart), which takes a NUMERIC asset_id — so the screener's
 * `S_NVDA.O` ids are first mapped via the global `assets-preview` list
 * (see StockTable/index.tsx).
 */
export interface StockChartPoint {
  price: string
  timestamp: number
}

// The 1D endpoint returns ~2-minute samples (600+ points per day) — far more
// than a 66x24 sparkline can show, so we downsample to this many points.
const SPARKLINE_SAMPLE_COUNT = 12

// chart API interval values used by the stock-table sparkline. `1D` is the
// daily intraday series, only populated during US market hours; `5D` covers
// ~5 sessions and stays populated off-hours. Decision 2026-09-07: the table
// probes 1D and falls back to 5D when the market is closed (weekend/凌晨),
// so the sparkline column is never empty — header label follows the mode.
export type StockSparklineInterval = '1D' | '5D'

async function fetchStockChart(
  assetId: string,
  interval: StockSparklineInterval,
): Promise<number[]> {
  try {
    const data = await equityMarketDataFetcher<StockChartPoint[]>(
      '/chart',
      { queries: { asset_id: assetId, interval } },
      { forceResponseType: 'json' },
    )
    const points = (data ?? [])
      .filter(point => point.timestamp && Number(point.price) > 0)
      .map(point => Number(point.price))
    if (points.length <= SPARKLINE_SAMPLE_COUNT) return points
    const step = points.length / SPARKLINE_SAMPLE_COUNT
    const sampled: number[] = []
    for (let i = 0; i < SPARKLINE_SAMPLE_COUNT; i += 1) {
      sampled.push(points[Math.floor(i * step)])
    }
    return sampled
  } catch {
    // Sparklines are per-row decorative: one failing asset must not break the table.
    return []
  }
}

export async function fetchStockSparkline(
  assetId: string,
  interval: StockSparklineInterval,
): Promise<number[]> {
  return fetchStockChart(assetId, interval)
}

/** 1D 有当日数据 = 交易时段（intraday feed 活跃）→ 用 1D，否则切 5D。 */
export async function probeStockSparklineInterval(
  assetId: string,
): Promise<StockSparklineInterval> {
  const points = await fetchStockChart(assetId, '1D')
  return points.length > 0 ? '1D' : '5D'
}

export const stockSparklineQueryOptions = (assetId: string, interval: StockSparklineInterval) =>
  queryOptions({
    queryKey: ['stockSparkline', assetId, interval] as const,
    queryFn: () => fetchStockSparkline(assetId, interval),
    staleTime: 60_000,
  })

export const useStockSparkline = (
  assetId: string,
  interval: StockSparklineInterval,
  enabled: boolean,
) => useQuery({ ...stockSparklineQueryOptions(assetId, interval), enabled })

// Probe: determine the off-hours fallback mode for the whole table from the
// first visible asset (1D empty on every asset is a market-wide condition).
export const stockSparklineProbeQueryOptions = (assetId: string) =>
  queryOptions({
    queryKey: ['stockSparklineProbe', assetId] as const,
    queryFn: () => probeStockSparklineInterval(assetId),
    staleTime: 60_000,
  })

export const useStockSparklineProbe = (assetId: string) => {
  const query = useQuery({
    ...stockSparklineProbeQueryOptions(assetId),
    enabled: assetId !== '',
  })
  return query.data ?? '1D'
}
